// Shared parsing + validation layer for bulk ad-unit (template) import.
// Turns pasted text or an uploaded file's contents into an array of mapped,
// validated ad-unit rows ready to send to the single-create endpoint. Supports
// a JSON array, a single JSON object, and CSV (header row → fields). CSV columns
// and JSON keys both flow through `mapJsonToAdUnit` so field-name variants keep
// working exactly like the one-at-a-time Paste JSON flow.

const FUNNEL_STAGES = ["Awareness", "Knowledge", "Liking", "Preference", "Conviction", "Purchase"] as const;

export function mapJsonToAdUnit(data: Record<string, any>): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (data.ad_unit_name) patch.name = data.ad_unit_name;
  if (data.name && !patch.name) patch.name = data.name;
  if (data.visual_identification) patch.visualIdentification = data.visual_identification;
  if (data.strategic_role?.description) patch.strategicRole = data.strategic_role.description;
  else if (typeof data.strategic_role === "string") patch.strategicRole = data.strategic_role;
  if (data.strategicRole && !patch.strategicRole) patch.strategicRole = data.strategicRole;
  if (data.primary_funnel) {
    const raw = String(data.primary_funnel).toLowerCase();
    const normalized = FUNNEL_STAGES.find(s => raw.includes(s.toLowerCase()))
      || (raw.includes("top") ? "Awareness" : null)
      || (raw.includes("mid") ? "Liking" : null)
      || (raw.includes("bottom") ? "Purchase" : null)
      || data.primary_funnel;
    patch.funnelStage = normalized;
  }
  if (data.funnelStage && !patch.funnelStage) patch.funnelStage = data.funnelStage;
  if (data.secondary) patch.secondaryFunnel = data.secondary;
  if (data.secondaryFunnel && !patch.secondaryFunnel) patch.secondaryFunnel = data.secondaryFunnel;
  if (data.objective) patch.objective = data.objective;
  if (data.psychological_function) patch.psychologicalFunction = data.psychological_function;
  if (data.psychologicalFunction && !patch.psychologicalFunction) patch.psychologicalFunction = data.psychologicalFunction;
  if (data.structural_drivers) patch.structuralDrivers = data.structural_drivers;
  if (data.structuralDrivers && !patch.structuralDrivers) patch.structuralDrivers = data.structuralDrivers;
  if (data.scroll_stopper) patch.scrollStopper = data.scroll_stopper;
  if (data.scrollStopper && !patch.scrollStopper) patch.scrollStopper = data.scrollStopper;
  if (data.core_message) patch.coreMessage = data.core_message;
  if (data.coreMessage && !patch.coreMessage) patch.coreMessage = data.coreMessage;
  if (data.psychological_levers) patch.psychologicalLevers = data.psychological_levers;
  if (data.psychologicalLevers && !patch.psychologicalLevers) patch.psychologicalLevers = data.psychologicalLevers;
  if (data.creative_elements) patch.creativeElements = data.creative_elements;
  if (data.creativeElements && !patch.creativeElements) patch.creativeElements = data.creativeElements;
  if (data.reusability_score !== undefined) patch.reusabilityScore = data.reusability_score;
  if (data.reusabilityScore !== undefined && !patch.reusabilityScore) patch.reusabilityScore = data.reusabilityScore;
  if (data.copy_framework) patch.copyFramework = data.copy_framework;
  if (data.copyFramework && !patch.copyFramework) patch.copyFramework = data.copyFramework;
  if (data.hook_style) patch.hookStyle = data.hook_style;
  if (data.hookStyle && !patch.hookStyle) patch.hookStyle = data.hookStyle;
  if (data.proof_patterns) patch.proofPatterns = data.proof_patterns;
  if (data.proofPatterns && !patch.proofPatterns) patch.proofPatterns = data.proofPatterns;
  if (data.cta_patterns) patch.ctaPatterns = data.cta_patterns;
  if (data.ctaPatterns && !patch.ctaPatterns) patch.ctaPatterns = data.ctaPatterns;
  if (data.visual_grammar) patch.visualGrammar = data.visual_grammar;
  if (data.visualGrammar && !patch.visualGrammar) patch.visualGrammar = data.visualGrammar;
  if (data.use_when) patch.useWhen = data.use_when;
  if (data.useWhen && !patch.useWhen) patch.useWhen = data.useWhen;
  if (data.avoid_when) patch.avoidWhen = data.avoid_when;
  if (data.avoidWhen && !patch.avoidWhen) patch.avoidWhen = data.avoidWhen;
  if (data.notes) patch.notes = data.notes;
  if (data.image_prompt_template) patch.imagePromptTemplate = data.image_prompt_template;
  if (data.imagePromptTemplate && !patch.imagePromptTemplate) patch.imagePromptTemplate = data.imagePromptTemplate;
  if (data.format_type) patch.formatType = data.format_type;
  if (data.formatType && !patch.formatType) patch.formatType = data.formatType;
  if (data.platform) patch.platform = data.platform;
  if (data.structure_template) patch.structureTemplate = data.structure_template;
  if (data.funnel_deployment) patch.funnelDeployment = data.funnel_deployment;
  if (data.ads_express_bucket) patch.adsExpressBucket = data.ads_express_bucket;
  if (data.adsExpressBucket && !patch.adsExpressBucket) patch.adsExpressBucket = data.adsExpressBucket;
  return patch;
}

export type BulkSourceFormat = "auto" | "json" | "csv";

export interface BulkParsedRow {
  index: number;
  mapped: Record<string, unknown>;
  name: string;
  formatType: string;
  funnelStage: string;
  valid: boolean;
  reason?: string;
}

export interface BulkParseResult {
  rows: BulkParsedRow[];
  error?: string;
}

// Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped quotes ("")
// inside quotes, and both \n and \r\n line breaks. Returns rows of string cells.
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; }
        else inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ",") { row.push(cell); cell = ""; continue; }
    if (ch === "\r") continue;
    if (ch === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; continue; }
    cell += ch;
  }
  // flush trailing cell/row
  if (cell.length > 0 || row.length > 0) { row.push(cell); rows.push(row); }
  return rows;
}

function csvToObjects(text: string): Record<string, any>[] {
  const grid = parseCsv(text).filter((r) => r.some((c) => c.trim() !== ""));
  if (grid.length < 2) return [];
  const headers = grid[0].map((h) => h.trim());
  return grid.slice(1).map((cells) => {
    const obj: Record<string, any> = {};
    headers.forEach((h, i) => {
      if (!h) return;
      const v = (cells[i] ?? "").trim();
      if (v !== "") obj[h] = v;
    });
    return obj;
  });
}

function looksLikeJson(text: string): boolean {
  const t = text.trim();
  return t.startsWith("[") || t.startsWith("{");
}

// Validation: a row must have a usable name. Format falls back to "static"
// (matching the schema default) so it is always usable, exactly like the
// single Paste JSON import which only enforces a name.
function toRow(raw: Record<string, any>, index: number): BulkParsedRow {
  const mapped = mapJsonToAdUnit(raw);
  const name = typeof mapped.name === "string" ? mapped.name.trim() : "";
  const formatType = (typeof mapped.formatType === "string" && mapped.formatType.trim()) || "static";
  const funnelStage = typeof mapped.funnelStage === "string" ? mapped.funnelStage : "";
  if (!name) {
    return { index, mapped, name: "", formatType, funnelStage, valid: false, reason: "Missing name" };
  }
  return { index, mapped: { ...mapped, formatType }, name, formatType, funnelStage, valid: true };
}

export function parseBulkAdUnits(input: string, format: BulkSourceFormat = "auto"): BulkParseResult {
  const trimmed = input.trim();
  if (!trimmed) return { rows: [] };

  const useCsv = format === "csv" || (format === "auto" && !looksLikeJson(trimmed));

  if (useCsv) {
    try {
      const objs = csvToObjects(input);
      if (objs.length === 0) return { rows: [], error: "No data rows found in the CSV (need a header row plus at least one row)." };
      return { rows: objs.map((o, i) => toRow(o, i)) };
    } catch (e: any) {
      return { rows: [], error: `Could not parse CSV: ${e?.message || "unknown error"}` };
    }
  }

  let data: unknown;
  try {
    data = JSON.parse(trimmed);
  } catch (e: any) {
    return { rows: [], error: `Invalid JSON: ${e?.message || "unknown error"}` };
  }
  const arr = Array.isArray(data) ? data : [data];
  const objects = arr.filter((d) => d && typeof d === "object" && !Array.isArray(d)) as Record<string, any>[];
  if (objects.length === 0) return { rows: [], error: "Expected a JSON object or an array of objects." };
  return { rows: objects.map((o, i) => toRow(o, i)) };
}
